import { useCallback, useEffect, useRef } from 'react';
import { SESSION_CONFIG, STORAGE_KEYS } from '../constants';
import { RememberMe } from '@mui/icons-material';

export const useSession = () => {
  const activityTimerRef = useRef(null);
  const sessionCheckIntervalRef = useRef(null);

  const initializeSession = useCallback((shouldRemember) => {
    const sessionConfig = {
      type: shouldRemember ? 'extended' : 'short',
      startTime: Date.now(),
      expiryTime: Date.now() + (shouldRemember ? SESSION_CONFIG.EXTENDED_SESSION : SESSION_CONFIG.SHORT_SESSION),
      lastActivity: Date.now(),
    };  
    if (shouldRemember) {
      localStorage.setItem(STORAGE_KEYS.SESSION_CONFIG, JSON.stringify(sessionConfig));
      localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
    } else {
      sessionStorage.setItem(STORAGE_KEYS.SESSION_CONFIG, JSON.stringify(sessionConfig));
      localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'false');
      if (!localStorage.getItem(STORAGE_KEYS.SHOW_STAY_LOGGED_IN_PROMPT)) {
        localStorage.setItem(STORAGE_KEYS.SHOW_STAY_LOGGED_IN_PROMPT, 'true');
      }
    }
  }, []);

  const updateActivity = useCallback(() => {
    const rememberMe = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
    const sessionConfigStr = rememberMe 
      ? localStorage.getItem(STORAGE_KEYS.SESSION_CONFIG)
      : sessionStorage.getItem(STORAGE_KEYS.SESSION_CONFIG);
    
    if (sessionConfigStr) {
      try {
        const sessionConfig = JSON.parse(sessionConfigStr);
        sessionConfig.lastActivity = Date.now();
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem(STORAGE_KEYS.SESSION_CONFIG, JSON.stringify(sessionConfig));
      } catch (e) {
        console.error('Failed to update session activity:', e);
      }
    }
  }, []);

  const isSessionValid = useCallback(() => {
    const rememberMe = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
    const sessionConfigStr = rememberMe 
      ? localStorage.getItem(STORAGE_KEYS.SESSION_CONFIG)
      : sessionStorage.getItem(STORAGE_KEYS.SESSION_CONFIG);
    
    if (!sessionConfigStr) return false;
    
    try {
      const sessionConfig = JSON.parse(sessionConfigStr);
      const isValid = Date.now() < sessionConfig.expiryTime;
      
      if (!isValid && !rememberMe) {
        sessionStorage.removeItem(STORAGE_KEYS.SESSION_CONFIG);
      }
      
      return isValid;
    } catch (e) {
      return false;
    }
  }, []);

  const getSessionRemainingTime = useCallback(() => {
    const rememberMe = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
    const sessionConfigStr = rememberMe 
      ? localStorage.getItem(STORAGE_KEYS.SESSION_CONFIG)
      : sessionStorage.getItem(STORAGE_KEYS.SESSION_CONFIG);
    
    if (!sessionConfigStr) return 0;
    
    try {
      const sessionConfig = JSON.parse(sessionConfigStr);
      return Math.max(0, sessionConfig.expiryTime - Date.now());
    } catch (e) {
      return 0;
    }
  }, []);

  const extendSession = useCallback(() => {
    const rememberMe = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
    const sessionConfigStr = rememberMe 
      ? localStorage.getItem(STORAGE_KEYS.SESSION_CONFIG)
      : sessionStorage.getItem(STORAGE_KEYS.SESSION_CONFIG);
    
    if (sessionConfigStr) {
      try {
        const sessionConfig = JSON.parse(sessionConfigStr);
        sessionConfig.expiryTime = Date.now() + (rememberMe ? SESSION_CONFIG.EXTENDED_SESSION : SESSION_CONFIG.SHORT_SESSION);
        sessionConfig.lastActivity = Date.now();
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem(STORAGE_KEYS.SESSION_CONFIG, JSON.stringify(sessionConfig));
        return true;
      } catch (e) {
        console.error('Failed to extend session:', e);
        return false;
      }
    }
    return false;
  }, []);

  useEffect(() => {
    sessionCheckIntervalRef.current = setInterval(() => {
      if (!isSessionValid()) {
        window.dispatchEvent(new CustomEvent('sessionExpired'));
      }
    }, 60000);
    
    return () => {
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }
    };
  }, [isSessionValid]);

  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'click', 'touchstart'];
    
    const handleActivity = () => {
      updateActivity();
      
      if (activityTimerRef.current) {
        clearTimeout(activityTimerRef.current);
      }
      
      activityTimerRef.current = setTimeout(() => {
        const rememberMe = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
        if (!rememberMe) {
          window.dispatchEvent(new CustomEvent('sessionExpired'));
        }
      }, 30 * 60 * 1000);
    };
    
    events.forEach(event => window.addEventListener(event, handleActivity));
    
    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      if (activityTimerRef.current) {
        clearTimeout(activityTimerRef.current);
      }
    };
  }, [updateActivity]);

  return { 
    initializeSession, 
    updateActivity, 
    isSessionValid, 
    getSessionRemainingTime,
    extendSession 
  };
};